import { Integrations } from './types';
import { getConfig } from 'src/config';
import type { SegmentIntegrationsConfig } from 'src/utils/segmentConfig';
import { segment } from './joy';

const config = getConfig();

export const addNewrelicAction = (actionName: string, value: Record<string, unknown>) => {
  if (typeof window !== 'undefined') {
    const valueWithPrefixes: Record<string, unknown> = {};

    for (const key in value) {
      valueWithPrefixes[`${actionName}_${key}`] = value[key];
    }

    window.newrelic?.addPageAction(actionName, valueWithPrefixes);
  }
};

export const initSegment = (integrationOverrides?: SegmentIntegrationsConfig) => {
  const global = window;

  // Set destination hostname for telemetry
  segment.setDestinationHostname();

  // Create a queue, but don't obliterate an existing one!
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const analytics = (window.analytics = (window as any).analytics || []);

  // If the real analytics.js is already on the page return.
  if (analytics?.initialize) return;

  // If the snippet was invoked already show an error.
  if (analytics?.invoked) {
    if (global.console && console.error) {
      console.error('Segment snippet included twice.');
    }
    return;
  }

  // Invoked flag, to make sure the snippet
  // is never invoked twice.
  analytics.invoked = true;

  // A list of the methods in Analytics.js to stub.
  analytics.methods = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'once',
    'off',
    'on',
    'addSourceMiddleware',
    'addIntegrationMiddleware',
    'setAnonymousId',
    'addDestinationMiddleware',
  ];

  // Define a factory to create stubs. These are placeholders
  // for methods in Analytics.js so that you never have to wait
  // for it to load to actually record data.
  // stored as the first argument, so we can replay the data.
  analytics.factory = function (method: unknown) {
    return function () {
      var args = Array.prototype.slice.call(arguments);
      args.unshift(method);
      if (analytics.indexOf(args) === -1) {
        analytics.push(args);
      }
      return analytics;
    };
  };

  // For each of our methods, generate a queueing stub.
  for (var i = 0; i < analytics.methods.length; i++) {
    var key = analytics.methods[i];
    analytics[key] = analytics.factory(key);
  }

  // Define a method to load Analytics.js from our CDN,
  // and that will be sure to only ever load it once.
  analytics.load = function (key: string, options: unknown) {
    var insertScript = function () {
      // Create an async script element based on your key.
      var script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = false;
      script.src = 'https://p.withjoy.com/segcdn/analytics.js/v1/' + key + '/analytics.min.js';
      // Append to head instead of inserting before first script to preserve SSR script order
      document.head.appendChild(script);
      analytics._loadOptions = options;
    };

    // Consent can arrive after the page has loaded: Polaris may report ready late, and
    // onConsentReady waits up to 250 ms for a placeholder cookie to be rewritten. A load
    // listener added then would never run, and Segment would never start on this page.
    if (document.readyState === 'complete') {
      insertScript();
    } else {
      global.addEventListener('load', insertScript);
    }
  };

  // Add a version to keep track of what's in the wild.
  analytics.SNIPPET_VERSION = '4.13.2';

  // Load Analytics.js with your key, which will automatically
  // load the tools you've enabled for your account. Boosh!

  var debuggerKey = '2xZJ98aly8fmfFDB9msDQlXsyVGVpUby';
  var prodKey = 'oHWqP4BrVeBDl1FxCTC45FfLZ0qnySFw';

  // Use provided integrations config (built from consent state in gatsby-browser.tsx)
  var analyticsConfig = {
    integrations: integrationOverrides || {},
  };

  if (global.location.hostname === 'withjoy.com') {
    // Prod
    analytics.load(prodKey, analyticsConfig);
  } else {
    // Running locally or Test environments like blue.withjoy.com, rehearsal.withjoy.com, etc
    analytics.load(debuggerKey, analyticsConfig);
  }

  if (global) {
    global.analytics = analytics;
  }
};

export function storeImpactRadiusInfo() {
  if (typeof window !== 'undefined') {
    var global = window;
    var queryString = global.location.search || '';
    var landingPage = global.location.pathname.replace(/\\|\//g, '');
    var matches = queryString.match(/[?&]irclickid=([^&]+)/i);
    var irClickId = matches && matches[1];
    if (irClickId) {
      var document = global.document;
      var existingCookies = document.cookie || '';
      if (existingCookies.search(/(^|;\s)joy_irclickid=/i) === -1) {
        var COOKIE_TTL_DAYS = 30;
        var expiration = new Date(new Date().getTime() + COOKIE_TTL_DAYS * 1000 * 60 * 60 * 24);
        document.cookie = 'joy_irclickid=' + irClickId + ';expires=' + expiration.toUTCString() + ';path=/;';
        document.cookie = 'joy_irtext1=' + landingPage + ';expires=' + expiration.toUTCString();
      }
    }
  }
}

/**
 * Resolves once we know whether an Amplitude Experiment client will exist for this page:
 * either `initAmplitudeExperiment` has finished assigning one, or `skipAmplitudeExperiment`
 * has recorded that consent was withheld.
 *
 * Consumers must not read `window.amplitudeExperimentClient` before this settles. Consent is
 * resolved asynchronously (a GPP round-trip) and the SDK itself is fetched on demand, so a
 * returning visitor whose `ajs_anonymous_id` is already in localStorage can otherwise reach
 * the flag loader first and silently resolve every flag to its default for the whole session.
 */
let settleAmplitudeExperiment: (() => void) | undefined;
const amplitudeExperimentSettled = new Promise<void>((resolve) => {
  settleAmplitudeExperiment = resolve;
});

export const whenAmplitudeExperimentSettled = (): Promise<void> => amplitudeExperimentSettled;

/** Record that no client is coming, so waiters stop waiting immediately. */
export const skipAmplitudeExperiment = () => {
  settleAmplitudeExperiment?.();
};

const initAmplitudeExperimentSdk = async () => {
  const global = window;

  // Loaded on demand: this runs only after Polaris reports `statistics` consent, so the SDK
  // stays out of the shared app bundle for every page load that never reaches this point.
  const { Experiment } = await import('@amplitude/experiment-js-client');

  const experimentSdk = Experiment.initialize(config.amplitudeExperimentApiKey, {
    serverUrl: config.amplitudeExperimentServer,
    flagsServerUrl: config.amplitudeExperimentFlagsServer,
    exposureTrackingProvider: {
      track: (exposure) => {
        window.analytics?.track?.(
          '$exposure',
          {
            category: '$exposure',
            action: '$exposure',
            flag_key: exposure.flag_key,
            variant: exposure.variant,
          },
          { integrations: { [Integrations.Amplitude]: true } }
        );
      },
    },
    automaticExposureTracking: false,
    fetchOnStart: true,
    pollOnStart: false,
  });

  global.amplitudeExperimentClient = experimentSdk;
  global.amplitudeExperimentClient.isFirstSdkLoad = true;
};

export const initAmplitudeExperiment = async () => {
  try {
    await initAmplitudeExperimentSdk();
  } catch (e) {
    // The chunk fetch can fail (network, ad blocker) and `Experiment.initialize` can throw. The
    // caller in `gatsby-browser` does not await this, so swallowing here is what keeps a failure
    // from surfacing as an unhandled rejection. Flag consumers already fall back to defaults.
    if (window.console && console.error) {
      console.error('Failed to initialize Amplitude Experiment.', e);
    }
  } finally {
    // Settle either way: a failed chunk fetch or SDK init must not leave flag consumers waiting.
    settleAmplitudeExperiment?.();
  }
};
